import React from "react";
import { motion } from "framer-motion";
import { Database, Boxes, Split, Workflow, PlayCircle, ScrollText, ShieldCheck, ArrowRight } from "lucide-react";
import { Seo } from "@/lib/seo";
import { PageHero } from "@/components/layout/PageHero";
import { Reveal, Stagger, staggerItem } from "@/components/motion/Reveal";
import { PlatformCta } from "@/components/sections/PlatformCta";
import { SectionHeading } from "@/components/ui/primitives";
import { useLang } from "@/i18n/LanguageContext";
import { deepLocalize } from "@/i18n/translate";

const flow = [
  { icon: Database, title: "Data", text: "Governed, traceable data inputs with provenance and lineage." },
  { icon: Boxes, title: "Models", text: "Versioned models catalogued in the AI Bill of Materials." },
  { icon: Split, title: "Decision", text: "Explainable decisions with recorded rationale and ownership." },
  { icon: Workflow, title: "Orchestration", text: "Governed workflows coordinating models, agents and services." },
  { icon: PlayCircle, title: "Execution", text: "Controlled execution under zero-trust access policies." },
  { icon: ScrollText, title: "Logs", text: "Immutable, tamper-evident operational logging." },
  { icon: ShieldCheck, title: "Audit", text: "Continuous audit readiness and durable evidence chains." },
];

const pillars = [
  {
    title: "Auditability by design",
    text: "Every action, approval and artefact becomes durable, reviewable evidence — not an afterthought assembled before an audit.",
  },
  {
    title: "Traceability end-to-end",
    text: "From data provenance to model version to decision rationale, the complete chain is recorded and verifiable.",
  },
  {
    title: "Sovereign & resilient",
    text: "Engineered for on-premise, sovereign-cloud and hybrid deployment, ensuring jurisdiction alignment and operational continuity.",
  },
];

export default function Technology() {
  const { lang, tr } = useLang();
  const steps = deepLocalize(flow, lang);
  const cols = deepLocalize(pillars, lang);
  return (
    <>
      <Seo
        title={tr("Technology — Verifiable AI Governance Architecture")}
        description={tr("The AstroLife technology architecture: data-driven orchestration with integrated governance, auditability and traceability across the AI lifecycle.")}
        path="/technology"
        keywords="Verifiable AI, AI Lifecycle, Trustworthy AI, Explainable AI, AI Audit, Immutable Audit Trail, Zero Trust"
      />

      <PageHero
        overline={tr("Technology")}
        title={tr("An engineered architecture for verifiable AI governance.")}
        subtitle={tr("Data-driven orchestration with integrated governance and auditability — from data ingestion to immutable audit, every stage is accountable.")}
        breadcrumbs={[{ label: tr("Technology") }]}
      />

      {/* Operational flow */}
      <section className="bg-navy py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <SectionHeading overline={tr("Operational Architecture")} title={tr("Seven stages. One accountable chain.")} />
          <div className="mt-14 space-y-3">
            {steps.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.04}>
                <div className="group flex items-center gap-6 rounded-2xl border border-navy-border bg-navy-surface p-6 transition-colors hover:border-astro-blue/40">
                  <span className="font-heading text-3xl font-black text-outline w-14 flex-shrink-0">{String(i + 1).padStart(2, "0")}</span>
                  <div className="inline-flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-astro-blue/10 text-astro-light">
                    <s.icon className="h-6 w-6" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-heading text-xl font-semibold text-white">{s.title}</h3>
                    <p className="mt-1 text-sm text-silver/60">{s.text}</p>
                  </div>
                  {i < flow.length - 1 && <ArrowRight className="hidden h-5 w-5 text-silver/30 md:block" />}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Pillars */}
      <section className="border-y border-navy-border bg-navy-surface py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <SectionHeading overline={tr("Engineering Principles")} title={tr("Built for the organisations where trust is non-negotiable.")} />
          <Stagger className="mt-14 grid gap-5 md:grid-cols-3">
            {cols.map((p) => (
              <motion.div key={p.title} variants={staggerItem} className="rounded-2xl border border-navy-border bg-navy p-8">
                <h3 className="font-heading text-xl font-semibold text-white">{p.title}</h3>
                <p className="mt-4 text-base leading-relaxed text-silver/70">{p.text}</p>
              </motion.div>
            ))}
          </Stagger>
        </div>
      </section>

      <PlatformCta />
    </>
  );
}
